/**
 * Choosing how the file tree is listed.
 *
 * DESIGN INTENT
 *   A sort control is read far more often than it is used, so the trigger
 *   states the current answer — "Modified ↓" — rather than being an anonymous
 *   icon you must open to interrogate. It sits in the files toolbar beside
 *   the filter because those are the two questions about the same list: which
 *   rows, and in what order.
 *
 *   Picking the key you are already on FLIPS the direction, which is what
 *   every file browser's column header does and what people try first. Picking
 *   a different key adopts that key's useful end (newest, biggest — see
 *   `defaultDirectionFor`), because "sort by date" almost always means "the
 *   recent stuff, at the top".
 *
 * DATA DEFINITION
 *   A KeyOption is { key, label, short, icon, hint }.
 *   Interpretation: one row of the menu — what the key is called in English,
 *   what it is called on the trigger where there is room for one word, the
 *   glyph that stands for it, and what the two directions of that key mean,
 *   since "ascending" says nothing useful about a date.
 */
import { useState, type ComponentType } from 'react'
import {
  ArrowUpNarrowWide, ArrowDownWideNarrow, CaseSensitive, Clock,
  CalendarPlus, HardDrive, Shapes, Check,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  type ProjectId, type SortKey, type SortDirection,
  SORT_KEYS, defaultDirectionFor,
} from '@lattice/core'
import { useUi, sortFor } from '@/store/ui'
import { useHotkeys } from '@/hooks/useHotkeys'
import { Button, Tooltip } from '@/components/ui/primitives'
import { cn } from '@/lib/utils'

interface KeyOption {
  readonly key: SortKey
  readonly label: string
  readonly short: string
  readonly icon: ComponentType<{ className?: string; strokeWidth?: number }>
  readonly hint: Readonly<Record<SortDirection, string>>
}

const OPTIONS: readonly KeyOption[] = [
  { key: 'name', label: 'Name', short: 'Name', icon: CaseSensitive, hint: { asc: 'A → Z', desc: 'Z → A' } },
  { key: 'modified', label: 'Date modified', short: 'Modified', icon: Clock, hint: { asc: 'Oldest first', desc: 'Newest first' } },
  { key: 'created', label: 'Date created', short: 'Created', icon: CalendarPlus, hint: { asc: 'Oldest first', desc: 'Newest first' } },
  { key: 'size', label: 'Size', short: 'Size', icon: HardDrive, hint: { asc: 'Smallest first', desc: 'Largest first' } },
  { key: 'kind', label: 'Kind', short: 'Kind', icon: Shapes, hint: { asc: 'Parts first', desc: 'Parts last' } },
]

const optionFor = (key: SortKey): KeyOption =>
  OPTIONS.find(o => o.key === key) ?? OPTIONS[0]

/**
 * The files toolbar's sort control: states how this project's tree is
 * currently listed, and changes it.
 * SortMenu : {projectId} -> Element
 */
export function SortMenu({ projectId }: { projectId: ProjectId }) {
  const order = useUi(s => sortFor(s, projectId))
  const setSort = useUi(s => s.setSort)
  const [open, setOpen] = useState(false)

  // "s" is free: App.tsx owns ⌘K/⌘P, ⌘U, ⌘⇧S, 1-3, F2, ⌫, / and Escape.
  // Bound here rather than in the global map because the control only exists
  // while the files view does, and it unbinds with it.
  useHotkeys([{ combo: 's', run: () => setOpen(o => !o), description: 'Sort files' }])

  const active = optionFor(order.key)
  const DirectionIcon = order.direction === 'asc' ? ArrowUpNarrowWide : ArrowDownWideNarrow

  const chooseKey = (key: SortKey): void => setSort(projectId, {
    key,
    direction: key === order.key
      ? order.direction === 'asc' ? 'desc' : 'asc'
      : defaultDirectionFor(key),
  })

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <Tooltip label={`Sorted by ${active.label.toLowerCase()} — ${active.hint[order.direction].toLowerCase()}`} keys={['S']}>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1 px-1.5"
            aria-label={`Sort files by ${active.label}, ${active.hint[order.direction]}`}
          >
            <DirectionIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span className="hidden md:inline">{active.short}</span>
          </Button>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end" sideOffset={6}
          className="pop-in z-50 min-w-52 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop"
        >
          <DropdownMenu.Label className="px-2 py-1 text-[11px] font-medium text-[var(--fg-subtle)]">
            Sort by
          </DropdownMenu.Label>

          <DropdownMenu.RadioGroup value={order.key}>
            {SORT_KEYS.map(key => {
              const option = optionFor(key)
              return (
                <DropdownMenu.RadioItem
                  key={key}
                  value={key}
                  onSelect={() => chooseKey(key)}
                  className={cn(
                    'snap flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none',
                    'data-[highlighted]:bg-[var(--bg-hover)]',
                  )}
                >
                  <option.icon className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
                  <span className="flex-1">{option.label}</span>
                  {key === order.key && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />}
                </DropdownMenu.RadioItem>
              )
            })}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--border)]" />

          <DropdownMenu.RadioGroup value={order.direction}>
            {(['asc', 'desc'] as const).map(direction => (
              <DropdownMenu.RadioItem
                key={direction}
                value={direction}
                onSelect={() => setSort(projectId, { key: order.key, direction })}
                className={cn(
                  'snap flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none',
                  'data-[highlighted]:bg-[var(--bg-hover)]',
                )}
              >
                {direction === 'asc'
                  ? <ArrowUpNarrowWide className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
                  : <ArrowDownWideNarrow className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />}
                <span className="flex-1">{active.hint[direction]}</span>
                {direction === order.direction && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--border)]" />
          <div className="px-2 py-1 text-[11px] text-[var(--fg-subtle)]">
            Folders stay grouped first.
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
